import { beschriftung, dezimaltrenner, katalog } from './sprache'
import { zellenschluessel } from './Aufriss'
import type { Zelle } from './Aufriss'
import type { Bericht, Periodenaufriss, Periodenbericht, Periodenzeile } from './typen'

/**
 * Die Suche im Bericht — alphanumerisch **und** nach Werten (H10).
 *
 * **Ein Feld, das die Eingabe selbst liest.** Buchstaben suchen in
 * Bezeichnungen und Codes, Ziffern suchen Beträge. Der Anwender soll nicht
 * vorher entscheiden müssen, wonach er sucht: Wer eine Differenz jagt, tippt
 * sie ein, und zwar so, wie sie in der Tabelle steht.
 *
 * **Gesucht wird über den ganzen Bericht, nicht über den Bildschirm.** Auch
 * die noch zugeklappten Konto- und Gesellschaftszellen zählen dazu — sie sind
 * ohnehin geladen. Genau dort steckt der gesuchte Betrag fast immer:
 *
 * > 1.100.000 gesucht, gefunden unter Vorräte → 1200 → NL-PROD.
 *
 * Der Treffer klappt seinen Pfad deshalb selbst auf. Was hier **nicht**
 * geschieht, ist die volle Ausbaustufe von H10: alle Perioden, alle Läufe, mit
 * Toleranz und Einbeziehungszustand. Die braucht den Parquet-Schnitt aus
 * Bereich 4 (ADR 003) und einen eigenen Endpunkt.
 */

/**
 * Eine Zahl der Tabelle mit ihrer Spalte.
 *
 * Die Spaltenkennung kommt aus dem Bildschirm, der das Suchgut baut — er
 * allein weiß, welche Zelle wo steht. Sie ist der Grund, warum die Suche nicht
 * nur die Zeile, sondern **die getroffene Zahl** hervorheben kann.
 */
export type Wertfeld = { spalte: string; wert: number }

/** Eine durchsuchbare Zeile: was sie an Text hergibt und welche Zahlen sie zeigt. */
export type Suchgut = {
  position: string
  pfad: Zelle[]
  /** Wie die Zeile heißt, wenn die Suche über sie berichtet. */
  name: string
  texte: string[]
  werte: Wertfeld[]
}

export type Fund = {
  id: string
  position: string
  pfad: Zelle[]
  name: string
  art: 'text' | 'wert'
  /** Der Betrag stimmt, aber mit umgekehrtem Vorzeichen. */
  umgekehrt: boolean
  /** Welche Spalten dieser Zeile den Betrag tragen — bei einem Texttreffer keine. */
  spalten: string[]
}

/** Die Kennung, unter der eine Zeile im Dokument steht — der Anker der Suche. */
export function zeilenId(position: string, pfad: Zelle[]): string {
  return ['z', position, ...pfad.map(zellenschluessel)].join('>')
}

/**
 * Groß- und Kleinschreibung und Akzente spielen keine Rolle: „vorrate" findet
 * „Vorräte".
 *
 * **Was hier ausdrücklich nicht geschieht, ist die Auflösung von ae/oe/ue.**
 * Genau daran wäre die Umlautkorrektur schon einmal gescheitert: Aus „Steuern"
 * würde „Stern". Wer „Vorraete" tippt, findet nichts — das ist der kleinere
 * Schaden.
 */
export function normalisiere(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ß/g, 'ss')
}

/**
 * Was der Anwender eingetippt hat, als Zahl gelesen — in **jeder** Schreibweise.
 *
 * Ein Berater, der seinen Bildschirm abwechselnd mit deutschen und englischen
 * Anwendern teilt, hat das Zahlenformat umgestellt, aber nicht seine Finger:
 * „1.100.000", „1,100,000" und „1100000" meinen dasselbe. Nur wo eine einzelne
 * Gruppe von genau drei Ziffern folgt — „1.100" —, ist die Eingabe wirklich
 * mehrdeutig; dort entscheidet das eingestellte Format.
 *
 * Die **Toleranz** kommt aus der Eingabe selbst: Wer Nachkommastellen tippt,
 * meint sie; wer „1200" tippt, meint einen gerundeten Betrag.
 */
export function wertLesen(eingabe: string): { wert: number; toleranz: number } | null {
  const roh = eingabe.replace(/[\s'’ ]/g, '')
  if (!/^[+-]?[\d.,]+$/.test(roh) || !/\d/.test(roh)) {
    return null
  }

  const letztesKomma = roh.lastIndexOf(',')
  const letzterPunkt = roh.lastIndexOf('.')
  let trenner: ',' | '.' | null = null

  if (letztesKomma >= 0 && letzterPunkt >= 0) {
    // Beide vorhanden: Der hintere ist das Dezimalzeichen, der vordere gruppiert.
    trenner = letztesKomma > letzterPunkt ? ',' : '.'
  } else if (letztesKomma >= 0 || letzterPunkt >= 0) {
    const zeichen = letztesKomma >= 0 ? ',' : '.'
    const anzahl = roh.split(zeichen).length - 1
    const nachkomma = roh.length - roh.lastIndexOf(zeichen) - 1

    if (anzahl > 1) {
      trenner = null
    } else if (nachkomma === 3) {
      // „1.100" — die einzige wirklich mehrdeutige Form. Hier entscheidet,
      // was der Anwender eingestellt hat, und sonst nichts.
      trenner = zeichen === dezimaltrenner() ? zeichen : null
    } else {
      trenner = zeichen
    }
  }

  const ziffern = trenner
    ? roh.split(trenner === ',' ? '.' : ',').join('').replace(trenner, '.')
    : roh.replace(/[.,]/g, '')

  const wert = Number(ziffern)
  if (!Number.isFinite(wert)) {
    return null
  }

  const stellen = trenner ? ziffern.length - ziffern.indexOf('.') - 1 : 0
  return { wert, toleranz: stellen > 0 ? 0.005 : 0.5 }
}

/**
 * Trifft dieser Betrag?
 *
 * **Die Null wird nicht gesucht.** Sie steht in jeder zweiten Zelle eines
 * Konzernabschlusses; eine Suche, die dreihundert Treffer meldet, hat nichts
 * gefunden.
 *
 * **Das umgekehrte Vorzeichen zählt als Treffer** und wird benannt. Wer einer
 * Differenz nachgeht, sucht die Gegenbuchung — und die steht mit dem anderen
 * Vorzeichen da (H10).
 */
export function trifftWert(
  kandidat: number,
  gesucht: number,
  toleranz: number,
): 'nein' | 'ja' | 'umgekehrt' {
  if (kandidat === 0) {
    return 'nein'
  }

  if (Math.abs(kandidat - gesucht) <= toleranz) {
    return 'ja'
  }

  return Math.abs(kandidat + gesucht) <= toleranz ? 'umgekehrt' : 'nein'
}

/**
 * Die Treffer, **nach Gruppen getrennt**: erst die Bezeichnungen, dann die
 * Beträge, innerhalb der Gruppe in der Reihenfolge des Berichts.
 *
 * Eine Eingabe wie „1200" ist beides — Kontonummer und Betrag. Statt zu raten,
 * führt die Suche beide Gruppen und sagt in der Statuszeile, in welcher man
 * sich gerade bewegt.
 */
export function suchen(gut: Suchgut[], eingabe: string): Fund[] {
  const nadel = normalisiere(eingabe.trim())
  if (nadel.length === 0) {
    return []
  }

  const zahl = wertLesen(eingabe)
  const texte: Fund[] = []
  const werte: Fund[] = []

  for (const g of gut) {
    const id = zeilenId(g.position, g.pfad)

    if (g.texte.some((x) => normalisiere(x).includes(nadel))) {
      texte.push({
        id,
        position: g.position,
        pfad: g.pfad,
        name: g.name,
        art: 'text',
        umgekehrt: false,
        spalten: [],
      })
      continue
    }

    if (!zahl) {
      continue
    }

    // Alle Spalten, nicht die erste: Steht der Betrag zweimal in der Zeile,
    // sind beide Zellen der Fund — und der Anwender sieht die Wiederholung,
    // statt sie zu suchen.
    let getroffen: 'nein' | 'ja' | 'umgekehrt' = 'nein'
    const spalten: string[] = []

    for (const w of g.werte) {
      const urteil = trifftWert(w.wert, zahl.wert, zahl.toleranz)
      if (urteil === 'nein') {
        continue
      }

      spalten.push(w.spalte)
      if (urteil === 'ja' || getroffen === 'nein') {
        getroffen = urteil
      }
    }

    if (getroffen !== 'nein') {
      werte.push({
        id,
        position: g.position,
        pfad: g.pfad,
        name: g.name,
        art: 'wert',
        umgekehrt: getroffen === 'umgekehrt',
        spalten,
      })
    }
  }

  return [...texte, ...werte]
}

/* ==========================================================================
   Was durchsucht wird
   ==========================================================================

   Je Spaltenbild andere Zahlen, aber dieselben Zeilen — deshalb bauen die
   Bildschirme ihr Suchgut selbst, und die Suche kennt keinen Bericht. */

/**
 * Der Pfad zu einer Aufrisszelle, in der Reihenfolge der Vorgabe: erst das
 * Konto, darin die Gesellschaft.
 */
function pfadZu(zelle: Zelle): Zelle[] {
  if (zelle.konto !== null && zelle.einheit !== null) {
    return [{ einheit: null, konto: zelle.konto }, { einheit: zelle.einheit, konto: zelle.konto }]
  }

  return [{ einheit: zelle.einheit, konto: zelle.konto }]
}

/** Wie eine Aufrisszelle heißt und woran man sie textlich findet. */
function zellentext(zelle: Zelle): { name: string; texte: string[] } {
  const nach = katalog()
  const teile: string[] = []
  const texte: string[] = []

  const stufen: [string | null, { code: string; namen: Record<string, string> } | undefined][] = [
    [zelle.konto, zelle.konto ? nach.konten.get(zelle.konto) : undefined],
    [zelle.einheit, zelle.einheit ? nach.einheiten.get(zelle.einheit) : undefined],
  ]

  for (const [kennung, eintrag] of stufen) {
    if (!kennung) {
      continue
    }

    const code = eintrag?.code ?? kennung
    const name = eintrag ? beschriftung(eintrag.namen) : ''
    teile.push(name ? `${code} · ${name}` : code)
    texte.push(code, name)
  }

  return { name: teile.join(' → '), texte }
}

/**
 * Das Suchgut der beiden Bilder, die auf demselben Bericht stehen.
 *
 * **Gesucht wird nur, was auch angezeigt wird** — und das ist je Bild etwas
 * anderes: Im Konsolidierungsbild trägt eine Aufrisszeile allein den
 * Konzernwert, im Strukturbild dazu die Handelsbilanz II in der Spalte ihrer
 * Gesellschaft. Ein Treffer in einer Zahl, die nirgends steht, wäre keiner.
 */
export function suchgutBericht(
  bericht: Bericht,
  bild: 'konsolidierung' | 'struktur',
  einheiten: string[],
): Suchgut[] {
  const gut: Suchgut[] = []

  for (const zeile of bericht.zeilen) {
    const name = `${zeile.code} ${beschriftung(zeile.namen)}`.trim()
    const jeEinheit = new Map(
      zeile.beitraege.filter((b) => b.konto === null && b.einheit).map((b) => [b.einheit, b.hb2]),
    )

    gut.push({
      position: zeile.position,
      pfad: [],
      name,
      texte: [zeile.code, beschriftung(zeile.namen)],
      werte:
        bild === 'struktur'
          ? [
              ...einheiten.map((e) => ({ spalte: `einheit:${e}`, wert: jeEinheit.get(e) ?? 0 })),
              { spalte: 'summe', wert: zeile.hb2 },
              { spalte: 'konsolidierung', wert: zeile.konzern - zeile.hb2 },
              { spalte: 'konzern', wert: zeile.konzern },
            ]
          : [
              { spalte: 'hb2', wert: zeile.hb2 },
              { spalte: 'kapko', wert: zeile.kapKo },
              { spalte: 'schuko', wert: zeile.schuKo },
              { spalte: 'aeko', wert: zeile.aeKo },
              { spalte: 'zwerge_uv', wert: zeile.zwErgEUv },
              { spalte: 'zwerge_av', wert: zeile.zwErgEAv },
              { spalte: 'konzern', wert: zeile.konzern },
            ],
    })

    // **Eine Summenzeile hat kein Suchgut unter sich** (R165, R432). Der
    // Bericht liefert ihr die Beiträge ihrer Summanden mit, weil die
    // Gesellschaftsspalten und die Nullzeilenunterdrückung daraus entstehen
    // (R421) — aufgerissen wird sie darum nicht. Stünden sie hier, meldete die
    // Suche einen Treffer auf einer Zeile, die kein Bildschirm zeichnen darf:
    // Der Zähler zählte ihn, die Statuszeile nennte seinen Weg, und der Sprung
    // ginge ins Leere. **Die Zeile selbst bleibt durchsuchbar** — ihr Betrag
    // steht da und darf gefunden werden.
    if (zeile.istSumme) {
      continue
    }

    for (const zelle of geordnet(zeile.beitraege)) {
      const beschriftet = zellentext(zelle)
      gut.push({
        position: zeile.position,
        pfad: pfadZu(zelle),
        name: `${name} → ${beschriftet.name}`,
        texte: beschriftet.texte,
        werte:
          bild === 'struktur'
            ? [
                ...einheiten.map((e) => ({
                  spalte: `einheit:${e}`,
                  wert: zelle.einheit === e ? zelle.hb2 : 0,
                })),
                { spalte: 'summe', wert: zelle.einheit === null ? zelle.hb2 : 0 },
                { spalte: 'konsolidierung', wert: zelle.konzern - zelle.hb2 },
                { spalte: 'konzern', wert: zelle.konzern },
              ]
            : [
                { spalte: 'hb2', wert: zelle.hb2 },
                { spalte: 'kapko', wert: zelle.kapKo },
                { spalte: 'schuko', wert: zelle.schuKo },
                { spalte: 'aeko', wert: zelle.aeKo },
                { spalte: 'zwerge_uv', wert: zelle.zwErgEUv },
                { spalte: 'zwerge_av', wert: zelle.zwErgEAv },
                { spalte: 'konzern', wert: zelle.konzern },
              ],
      })
    }
  }

  return gut
}

/**
 * Die Kennung einer Periodenspalte — ihre Stelle, nicht ihre Periode: In der
 * Bewegungsbilanz steht an Stelle 0 die Veränderung der zweiten Periode.
 */
export function periodenspalte(stelle: number): string {
  return `periode:${stelle}`
}

/** Das Suchgut des Periodenbildes — mit den Zahlen, die dort wirklich stehen. */
export function suchgutPerioden(
  bericht: Periodenbericht,
  /** Die angezeigten Werte einer Zelle: in der Bewegungsbilanz andere als die Bestände. */
  werteVon: (zeile: Periodenzeile, zelle: Periodenaufriss | null) => number[],
): Suchgut[] {
  const gut: Suchgut[] = []
  const mitSpalte = (werte: number[]): Wertfeld[] =>
    werte.map((wert, i) => ({ spalte: periodenspalte(i), wert }))

  for (const zeile of bericht.zeilen) {
    const name = `${zeile.code} ${beschriftung(zeile.namen)}`.trim()
    // Die Zeile heißt im Zeitvergleich nach ihrer Kennung (R1723) — sonst
    // spränge ein Treffer auf der alten Stelle einer umgehängten Position zur neuen.
    const kennung = zeile.kennung ?? zeile.position
    gut.push({
      position: kennung,
      pfad: [],
      name,
      texte: [zeile.code, beschriftung(zeile.namen)],
      werte: mitSpalte(werteVon(zeile, null)),
    })

    // **Eine Summenzeile hat kein Suchgut unter sich** (R165, R432). Der
    // Bericht liefert ihr die Beiträge ihrer Summanden mit, weil die
    // Gesellschaftsspalten und die Nullzeilenunterdrückung daraus entstehen
    // (R421) — aufgerissen wird sie darum nicht. Stünden sie hier, meldete die
    // Suche einen Treffer auf einer Zeile, die kein Bildschirm zeichnen darf:
    // Der Zähler zählte ihn, die Statuszeile nennte seinen Weg, und der Sprung
    // ginge ins Leere. **Die Zeile selbst bleibt durchsuchbar** — ihr Betrag
    // steht da und darf gefunden werden.
    if (zeile.istSumme) {
      continue
    }

    for (const zelle of geordnet(zeile.beitraege)) {
      const beschriftet = zellentext(zelle)
      gut.push({
        position: kennung,
        pfad: pfadZu(zelle),
        name: `${name} → ${beschriftet.name}`,
        texte: beschriftet.texte,
        werte: mitSpalte(werteVon(zeile, zelle)),
      })
    }
  }

  return gut
}

/**
 * Die Zellen in der Reihenfolge, in der sie aufgeklappt erscheinen: erst das
 * Konto, darunter seine Gesellschaften, zuletzt die Gesellschaften ohne Konto.
 */
function geordnet<T extends Zelle>(zellen: T[]): T[] {
  const konten = zellen.filter((z) => z.konto !== null && z.einheit === null)
  const beides = zellen.filter((z) => z.konto !== null && z.einheit !== null)
  const einheiten = zellen.filter((z) => z.konto === null && z.einheit !== null)

  return [...konten.flatMap((k) => [k, ...beides.filter((b) => b.konto === k.konto)]), ...einheiten]
}
